import { useEffect, useEffectEvent, useRef, useState } from "react";

import { useTranslation } from "react-i18next";

import { useInfiniteQuery, useMutation } from "@tanstack/react-query";
import { InboxIcon, PlusIcon } from "lucide-react";

import { hasPermission } from "simplydo/core/permissions";
import { asMutation, initUseQuery, queryKey } from "simplydo/core/query";
import { useCachedState } from "simplydo/react/hooks";
import { RichText } from "simplydo/react/richtext";
import { styled } from "simplydo/react/styled";
import { Button } from "simplydo/react/ui/button";
import {
  Checkbox,
  CheckboxContent,
  CheckboxHeader,
} from "simplydo/react/ui/checkbox";
import { Divider } from "simplydo/react/ui/divider";
import { Dropdown } from "simplydo/react/ui/dropdown";
import { H3, H4, Subheader } from "simplydo/react/ui/header";
import { Icon } from "simplydo/react/ui/icon";
import {
  Modal,
  ModalActions,
  ModalContent,
  ModalHeader,
} from "simplydo/react/ui/modal";
import {
  Placeholder,
  PlaceholderLine,
  PlaceholderParagraph,
} from "simplydo/react/ui/placeholder";

import api from "api";
import { useAppSelector } from "store";

import AchievementFeedItem from "./FeedItems/AchievementFeedItem";
import ChallengeFeedItem from "./FeedItems/ChallengeFeedItem";
import FollowedChallengeSubmittedIdeaFeedItem from "./FeedItems/FollowedChallengeSubmittedIdeaFeedItem";
import GroupActivityFeedPostFeedItem from "./FeedItems/GroupActivityFeedPostFeedItem";
import GroupDiscussionFeedItem from "./FeedItems/GroupDiscussionFeedItem";
import IdeaCommentFeedItem from "./FeedItems/IdeaCommentFeedItem";
import IdeaFeedItem from "./FeedItems/IdeaFeedItem";
import OrganisationActivityFeedPostFeedItem from "./FeedItems/OrganisationActivityFeedPostFeedItem";

const feedOptions = [
  { key: "idea", text: "$t(generic.ideas,capitalise)", value: "idea" },
  {
    key: "challenge",
    text: "$t(generic.challenges,capitalise)",
    value: "challenge",
  },
  {
    key: "achievement",
    text: "$t(generic.achievements,capitalise)",
    value: "achievement",
  },
  {
    key: "organisationActivityFeedPosts",
    text: "$t(generic.organisations,capitalise)",
    value: "organisationActivityFeedPosts",
  },
  { key: "group", text: "$t(generic.groups,capitalise)", value: "group" },
];
const feedOptionsWithoutAchievements = feedOptions.filter(
  (option) => option.value !== "achievement",
);
const allFeedValues = feedOptions.map((option) => option.value);
const feedValuesWithoutAchievements = feedOptionsWithoutAchievements.map(
  (option) => option.value,
);
const FEED_PAGE_SIZE = 10;
const LOAD_MORE_ROOT_MARGIN = "0px 0px 80px 0px";

const scrollParent = (node: HTMLElement): Element | null => {
  let current = node.parentElement;
  while (current) {
    const { overflowY } = getComputedStyle(current);
    if (
      (overflowY === "auto" || overflowY === "scroll") &&
      current.scrollHeight > current.clientHeight + 8
    ) {
      return current;
    }
    current = current.parentElement;
  }
  return null;
};

const LoadMoreSentinel = styled.div`
  min-height: 1px;
  overflow-anchor: none;

  &,
  & * {
    overflow-anchor: none;
  }
`;

const EmptyFeed = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  padding: 3rem 1rem;
  text-align: center;
`;

const EmptyFeedIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.primary.solid};
  background-color: ${({ theme }) => theme.colors.primary.softer};
`;

const FeedItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
  max-height: 100%;
  min-width: 0;
  width: calc(100% + 12px);
  padding-right: 12px;
  overflow: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(0, 0, 0, 0.2) transparent;

  &:hover {
    scrollbar-color: rgba(0, 0, 0, 0.35) transparent;
  }

  > div {
    position: relative;
    min-width: 0;
    overflow-wrap: anywhere;

    &:not(:last-child):before {
      content: "";
      position: absolute;
      bottom: -1rem;
      left: 0;
      width: 100%;
      height: 1px;
      border-bottom: 1px solid ${({ theme }) => theme.colors.primary.softer};
    }
  }
`;

const ActivityFeedBlock = () => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const achievementsEnabled =
    user?.ownerOrganisation?.achievementsEnabled !== false;
  const visibleFeedOptions = achievementsEnabled
    ? feedOptions
    : feedOptionsWithoutAchievements;
  const visibleFeedValues = achievementsEnabled
    ? allFeedValues
    : feedValuesWithoutAchievements;

  const [include, setInclude] = useState<string>("all");

  const [postEditor, setPostEditor] = useState({
    isOpen: false,
    isPinned: false,
    isAnonymous: false,
  });

  const [message, setMessage] = useCachedState<string>(
    user?._id && user.organisation
      ? {
          kind: "orgFeedPost",
          forType: "organisation",
          forId: user.organisation,
          user: user._id,
        }
      : undefined,
    "",
  );

  const createPost = useMutation({
    mutationFn: asMutation(api.organisations.postToActivityFeed)!,
    onSuccess: (data, variables) => {
      setPostEditor({ isOpen: false, isPinned: false, isAnonymous: false });
      setMessage("");
      api.setInfiniteQueryData(api.users.feed, [], (prev) => ({
        ...prev,
        pages: prev.pages.map((page, index) => ({
          ...page,
          feed:
            index === 0
              ? [
                  {
                    ...data.post,
                    type: "organisationActivityFeedPost",
                    owner: variables[1].isAnonymous ? undefined : user!,
                  },
                  ...page.feed,
                ]
              : page.feed,
        })),
      }));
    },
  });

  const feedQuery = useInfiniteQuery({
    queryKey: queryKey(api.users.feed, [
      {
        include: include === "all" ? visibleFeedValues : [include],
        page: 1,
        limit: FEED_PAGE_SIZE,
      },
    ]),
    queryFn: ({ pageParam }) =>
      initUseQuery(api.users.feed, [
        {
          include: include === "all" ? visibleFeedValues : [include],
          page: pageParam,
          limit: FEED_PAGE_SIZE,
        },
      ]).queryFn(),
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      if (
        !lastPage.canExtend ||
        lastPage.feed.length === 0 ||
        lastPage.feed.length < FEED_PAGE_SIZE
      ) {
        return undefined;
      }
      return lastPageParam + 1;
    },
  });

  const feedRef = useRef<HTMLDivElement>(null);
  const loadMoreSentinelRef = useRef<HTMLDivElement>(null);
  const loadMore = useEffectEvent(() => {
    if (
      !feedQuery.hasNextPage ||
      feedQuery.isFetchingNextPage ||
      feedQuery.isFetchNextPageError
    ) {
      return;
    }
    void feedQuery.fetchNextPage();
  });

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    const feed = feedRef.current;
    if (!sentinel || !feedQuery.hasNextPage) return;

    const root =
      feed && feed.scrollHeight > feed.clientHeight + 8
        ? feed
        : scrollParent(sentinel);

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) loadMore();
      },
      { root, rootMargin: LOAD_MORE_ROOT_MARGIN },
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [feedQuery.hasNextPage, feedQuery.dataUpdatedAt]);

  if (feedQuery.isLoading)
    return (
      <Placeholder>
        <PlaceholderParagraph>
          <PlaceholderLine style={{ height: 20 }} length="medium" />
          <PlaceholderLine style={{ height: 20 }} />
          <PlaceholderLine style={{ height: "60vh" }} />
        </PlaceholderParagraph>
      </Placeholder>
    );

  const feedItems = feedQuery.data?.pages.flatMap((page) => page.feed) ?? [];
  const reachedEnd =
    !feedQuery.hasNextPage &&
    !feedQuery.isFetchingNextPage &&
    feedItems.length > 0;

  return (
    <div
      style={{
        height: "100%",
        minWidth: 0,
        display: "flex",
        flexDirection: "column",
        gap: 12,
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
        }}
      >
        <div>
          <H3>
            Activity feed
            <Subheader>The latest updates for you.</Subheader>
          </H3>
        </div>
        <Dropdown
          value={include || "all"}
          style={{ marginTop: 2 }}
          onChange={(e, { value }) => setInclude(value as string)}
          options={[
            { key: "all", text: "All", value: "all" },
            ...visibleFeedOptions.map((option) => ({
              ...option,
              text: t(option.text),
            })),
          ]}
        />
      </div>
      {hasPermission(user, "org.viewDashboard", user!.organisation) ? (
        <div>
          <Button
            size="small"
            color="primary"
            variant="soft"
            fluid
            onClick={() => setPostEditor((prev) => ({ ...prev, isOpen: true }))}
          >
            <Icon icon={PlusIcon} /> New post
          </Button>
        </div>
      ) : null}
      <Modal
        open={postEditor.isOpen}
        onClose={() => setPostEditor((prev) => ({ ...prev, isOpen: false }))}
      >
        <ModalHeader>New activity feed post</ModalHeader>
        <ModalContent>
          <RichText
            preset="simple"
            value={message}
            onChange={(value) => setMessage(value)}
          />
          <Divider hidden />
          <p style={{ marginBottom: 12 }}>
            These posts will show to users of this organisation in their
            activity feed. By default, they will appear at the top of the feeds
            for 7 days, after which they will filter into the timeline.
          </p>

          <Checkbox
            checked={postEditor.isPinned}
            onChange={() =>
              setPostEditor((prev) => ({ ...prev, isPinned: !prev.isPinned }))
            }
          >
            <CheckboxHeader>Pin to top</CheckboxHeader>
            <CheckboxContent>
              Toggle this on to pin the post to the top of all users&apos;
              activity feeds. If multiple posts are pinned, they will be ordered
              by recency.
            </CheckboxContent>
          </Checkbox>
          <Checkbox
            checked={postEditor.isAnonymous}
            onChange={() =>
              setPostEditor((prev) => ({
                ...prev,
                isAnonymous: !prev.isAnonymous,
              }))
            }
          >
            <CheckboxHeader>Post anonymously</CheckboxHeader>
            <CheckboxContent>
              Toggle this on to post anonymously. The organisation name will be
              shown as the author.
            </CheckboxContent>
          </Checkbox>
        </ModalContent>
        <ModalActions>
          <Button
            onClick={() =>
              setPostEditor((prev) => ({ ...prev, isOpen: false }))
            }
          >
            Cancel
          </Button>
          <Button
            color="primary"
            disabled={!message}
            loading={createPost.isPending}
            onClick={() => {
              createPost.mutate([
                user!.organisation,
                {
                  message,
                  isPinned: postEditor.isPinned,
                  isAnonymous: postEditor.isAnonymous,
                },
              ]);
            }}
          >
            Post
          </Button>
        </ModalActions>
      </Modal>
      {feedItems.length === 0 && !feedQuery.isError ? (
        <EmptyFeed>
          <EmptyFeedIcon>
            <Icon icon={InboxIcon} />
          </EmptyFeedIcon>
          <H4 style={{ margin: 0 }}>
            {t("home.activityFeed.empty.title")}
            <Subheader>
              {t(
                include === "all"
                  ? "home.activityFeed.empty.info"
                  : "home.activityFeed.empty.filteredInfo",
              )}
            </Subheader>
          </H4>
        </EmptyFeed>
      ) : (
        <FeedItemsContainer ref={feedRef}>
          {feedItems.map((item) => {
            const key = `${item.type}-${item._id}`;
            switch (item.type) {
              case "groupFeedComment":
                return <GroupDiscussionFeedItem key={key} item={item} />;
              case "idea":
                return <IdeaFeedItem key={key} item={item} />;
              case "challenge":
              case "discoverChallenge":
                return <ChallengeFeedItem key={key} item={item} />;
              case "followedChallengeSubmittedIdea":
                return (
                  <FollowedChallengeSubmittedIdeaFeedItem
                    key={key}
                    item={item}
                  />
                );
              case "ideaComment":
                return <IdeaCommentFeedItem key={key} item={item} />;
              case "achievement":
                return achievementsEnabled ? (
                  <AchievementFeedItem key={key} item={item} />
                ) : null;
              case "organisationActivityFeedPost":
                return (
                  <OrganisationActivityFeedPostFeedItem key={key} item={item} />
                );
              case "groupActivityFeedPost":
                return <GroupActivityFeedPostFeedItem key={key} item={item} />;
              default:
                return null;
            }
          })}
          {feedQuery.hasNextPage ? (
            <LoadMoreSentinel ref={loadMoreSentinelRef}>
              <Button
                fluid
                size="small"
                color="primary"
                variant="ghost"
                onClick={() => feedQuery.fetchNextPage()}
                loading={feedQuery.isFetchingNextPage}
              >
                {t("home.activityFeed.loadMore")}
              </Button>
            </LoadMoreSentinel>
          ) : reachedEnd ? (
            <Subheader style={{ textAlign: "center", margin: "0.5rem 0" }}>
              {t("home.activityFeed.end")}
            </Subheader>
          ) : null}
        </FeedItemsContainer>
      )}
    </div>
  );
};

export default ActivityFeedBlock;
